<template>
  <div class="chat-container">
    <header class="chat-header">
      <div class="header-content">
        <h1>GuardBot</h1>
        <p>Your AI Fraud Detection Assistant</p>
      </div>
      <button @click="resetChat" class="reset-button" title="Mulai sesi baru">
        <IconReset />
      </button>
    </header>

    <main class="chat-window" ref="chatWindow">
      <div class="message received" v-if="messages.length === 0">
        <div class="message-content">
          <div class="message-title">Selamat Datang di GuardBot! 👋</div>
          <div class="message-analysis">
            Apa kabar? Semoga sehat dan aman selalu, ya! Saya siap membantu anda, kirimkan foto atau teks yang mencurigakan untuk saya telaah.
          </div>
        </div>
      </div>
      
      <div v-for="(msg, index) in messages" :key="index" :class="['message', msg.sender]">
        <div class="message-content" v-if="msg.sender === 'user'">
          <div v-if="msg.text">{{ msg.text }}</div>
          <img v-if="msg.image" :src="msg.image" alt="Screenshot" class="message-image" />
        </div>
        
        <div class="message-content" v-else>
          <div class="message-title" :class="msg.content.type === 'fraud_warning' ? 'fraud' : ''">{{ msg.content.title }}</div>
          <div class="message-analysis" v-html="formatResponseText(msg.content.analysis)"></div>
          <ul v-if="msg.content.recommendations && msg.content.recommendations.length" class="recommendations">
            <li v-for="(rec, i) in msg.content.recommendations" :key="i">{{ rec }}</li>
          </ul>
        </div>
      </div>
      
      <div v-if="isLoading" class="message received">
        <div class="message-content">
          <div class="typing-indicator"><span></span><span></span><span></span></div>
        </div>
      </div>
    </main>

    <footer class="chat-input-area">
      <input type="file" ref="fileInput" @change="handleFileSelect" accept="image/*" style="display: none" />
      <button @click="triggerFileUpload" class="icon-button" title="Unggah Screenshot">
        <IconUpload />
      </button>
      <input 
        type="text" 
        v-model="newMessage" 
        @keyup.enter="handleSend" 
        placeholder="Ketik pesan atau unggah screenshot..."
      />
      <button @click="handleSend" class="send-button" title="Kirim">
        <IconSend />
      </button>
    </footer>
  </div>
</template>

<script setup>
import { ref, nextTick } from 'vue';
import IconSend from './icons/IconSend.vue';
import IconUpload from './icons/IconUpload.vue';
import IconReset from './icons/IconReset.vue';

// --- ⚠️ INSECURE CONFIGURATION ⚠️ ---
// Kunci API Anda akan terlihat oleh semua orang di browser.
const GEMINI_API_KEY = "AIzaSyDUiHJ48Dh350nzObIUOzB5M_BqSjgp1AQ";
// --- END OF INSECURE CONFIGURATION ---

const API_URL_TEXT = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${GEMINI_API_KEY}`;
const API_URL_VISION = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${GEMINI_API_KEY}`;

// --- PROMPT ENGINEERING (Moved from Backend) ---
const fraudAnalysisPromptTemplate = `
Anda adalah GuardBot, seorang ahli keamanan siber dan analis penipuan keuangan.
Tugas Anda adalah menganalisis teks yang diberikan oleh pengguna dan menentukan apakah itu adalah upaya penipuan (fraud/scam) atau pertanyaan keuangan yang sah.

Berdasarkan analisis Anda, berikan respons HANYA dalam format JSON yang valid, dengan struktur sebagai berikut:
{
  "type": "fraud_warning" | "education",
  "title": "<Judul singkat yang jelas, misal: '⚠️ Peringatan Penipuan!' atau 'Edukasi Keuangan'>",
  "analysis": "<Penjelasan detail tentang mengapa teks ini terindikasi penipuan ATAU jawaban edukatif atas pertanyaan pengguna. Gunakan bahasa yang mudah dimengerti. Jelaskan tanda-tandanya seperti urgensi palsu, tautan mencurigakan, permintaan data pribadi, dll.>",
  "recommendations": [
    "<Rekomendasi langkah pertama yang harus dilakukan pengguna>",
    "<Rekomendasi langkah kedua>",
    "<Rekomendasi langkah selanjutnya...>"
  ]
}

- Jika ini adalah penipuan, set 'type' ke 'fraud_warning'. 'analysis' harus menjelaskan ciri-ciri penipuannya. 'recommendations' harus berisi langkah-langkah konkret seperti "Jangan klik tautan apapun", "Blokir nomor pengirim", "Laporkan ke pihak berwenang".
- Jika ini adalah pertanyaan keuangan biasa (misal: "bagaimana cara berinvestasi?"), set 'type' ke 'education'. 'analysis' harus menjawab pertanyaan tersebut secara edukatif. 'recommendations' bisa berisi saran praktis terkait pertanyaan.
- Jika teks tidak jelas atau tidak relevan, berikan respons edukasi umum tentang cara mengenali penipuan.

Riwayat percakapan sebelumnya (jika ada) akan diberikan oleh sistem. Fokus pada input terakhir dari pengguna.
Teks untuk dianalisis adalah sebagai berikut:
---
"{INPUT_TEXT}"
---
`;

// --- State Management ---
const messages = ref([]);
const newMessage = ref('');
const isLoading = ref(false);
const chatWindow = ref(null);
const chatHistory = ref([]); // Local chat history

// --- Utility Methods ---
const scrollToBottom = () => nextTick(() => { if (chatWindow.value) chatWindow.value.scrollTop = chatWindow.value.scrollHeight; });

/**
 * Memformat teks respons dari AI.
 * 1. Mengubah **teks tebal** menjadi tag <strong>.
 * 2. Mengubah karakter baris baru (\n) menjadi tag <br>.
 * @param {string} text - Teks yang akan diformat.
 * @returns {string} Teks yang sudah diformat dalam HTML.
 */
const formatResponseText = (text) => {
  if (!text) return '';
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Mengubah **teks** menjadi tebal
    .replace(/\n/g, '<br />'); // Mengubah baris baru menjadi <br>
};

// --- Core Logic ---
const handleSend = () => {
  const text = newMessage.value.trim();
  if (!text) return;
  
  messages.value.push({ sender: 'user', text: text });
  newMessage.value = '';
  
  processRequest(text);
};

const handleFileSelect = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (e) => {
    messages.value.push({ sender: 'user', text: file.name, image: e.target.result });
    const base64Data = e.target.result.split(',')[1];
    processRequest(null, { mimeType: file.type, data: base64Data });
  };
  reader.readAsDataURL(file);
};

const processRequest = async (text, image = null) => {
  isLoading.value = true;
  scrollToBottom();

  try {
    let textToAnalyze = text;

    // 1. If there's an image, perform OCR first
    if (image) {
      const ocrPrompt = "Ekstrak semua teks yang terlihat dari gambar ini. Kembalikan hanya teks mentahnya tanpa tambahan apapun.";
      const requestBody = {
        contents: [{
          parts: [{ text: ocrPrompt }, { inline_data: { mime_type: image.mimeType, data: image.data } }]
        }]
      };
      const response = await fetch(API_URL_VISION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      if (!response.ok) throw new Error(`Vision API error! Status: ${response.status}`);
      const data = await response.json();
      textToAnalyze = data.candidates[0].content.parts[0].text;
    }
    
    if (!textToAnalyze || !textToAnalyze.trim()) {
      throw new Error("Tidak ada teks untuk dianalisis.");
    }

    // 2. Analyze the text (from OCR or direct input)
    const analysisPrompt = fraudAnalysisPromptTemplate.replace('{INPUT_TEXT}', textToAnalyze);
    
    chatHistory.value.push({ role: 'user', parts: [{ text: analysisPrompt }]});

    const textResponse = await fetch(API_URL_TEXT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contents: chatHistory.value })
    });
    if (!textResponse.ok) throw new Error(`Text API error! Status: ${textResponse.status}`);
    const textData = await textResponse.json();
    
    if (!textData.candidates || textData.candidates.length === 0) {
      throw new Error("Respons diblokir karena alasan keamanan. Coba dengan input yang berbeda.");
    }
    
    const responseText = textData.candidates[0].content.parts[0].text;
    chatHistory.value.push({ role: 'model', parts: [{ text: responseText }]});

    // Parse the JSON string from the response
    const jsonString = responseText.replace(/```json\n|```/g, '').trim();
    const parsedResponse = JSON.parse(jsonString);

    messages.value.push({ sender: 'received', content: parsedResponse });

  } catch (error) {
    console.error("Frontend API call failed:", error);
    messages.value.push({
      sender: 'received',
      content: { title: 'Terjadi Kesalahan', analysis: error.message, recommendations: ["Pastikan API Key valid dan coba lagi."] }
    });
  } finally {
    isLoading.value = false;
    scrollToBottom();
  }
};

const resetChat = () => {
  messages.value = [];
  chatHistory.value = []; // Clear local history
  isLoading.value = false;
};

// --- Helper for file input ---
const triggerFileUpload = () => {
  const el = document.querySelector('input[type="file"]');
  if (el) el.value = '';
  el.click();
};
</script>

<style lang="scss">
:root {
  --gojek-green: #00880C;
  --gojek-red: #D92525;
  --background-light: #F8F8F8;
  --bubble-sent-bg: #E1F8E2;
  --bubble-received-bg: #FFFFFF;
  --text-primary: #333333;
  --text-secondary: #666666;
  --border-color: #EFEFEF;
}

.chat-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  background-color: var(--background-light);
  font-family: 'Inter', sans-serif;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: var(--gojek-green);
  color: white;
  padding: 16px 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  z-index: 10;
}

.header-content {
  text-align: left;
  
  h1 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
  }
  
  p {
    margin: 0;
    opacity: 0.9;
    font-size: 0.85rem;
  }
}

.reset-button {
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.3);
  color: white;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  flex-shrink: 0;
  
  &:hover {
    background: rgba(255,255,255,0.2);
  }
}

.chat-window {
  flex-grow: 1;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.message {
  display: flex;
  max-width: 85%;
  
  &.user {
    align-self: flex-end;
    
    .message-content {
      background-color: var(--bubble-sent-bg);
      color: var(--text-primary);
      border-radius: 18px 18px 4px 18px;
    }
  }
  
  &.received {
    align-self: flex-start;
    
    .message-content {
      background-color: var(--bubble-received-bg);
      border: 1px solid var(--border-color);
      border-radius: 18px 18px 18px 4px;
    }
  }
}

.message-content {
  padding: 12px 16px;
  font-size: 0.95rem;
  line-height: 1.5;
  word-wrap: break-word;
  
  .message-image {
    display: block;
    max-width: 120px;
    height: auto;
    border-radius: 8px;
    margin-top: 8px;
    object-fit: cover;
  }
}

.message-title {
  font-weight: 700;
  margin-bottom: 8px;
  color: var(--gojek-green);
  
  &.fraud {
    color: var(--gojek-red);
  }
}

.recommendations {
  padding-left: 20px;
  margin-top: 12px;
  margin-bottom: 0;
  font-size: 0.9rem;
  color: var(--text-secondary);
  
  li {
    margin-bottom: 6px;
  }
}

.chat-input-area {
  display: flex;
  align-items: center;
  padding: 12px;
  background-color: #FFFFFF;
  border-top: 1px solid var(--border-color);
  
  input[type="text"] {
    flex-grow: 1;
    border: none;
    background-color: var(--background-light);
    padding: 12px 16px;
    border-radius: 22px;
    font-size: 1rem;
    outline: none;
    margin: 0 8px;
    transition: box-shadow 0.2s;
    
    &:focus {
      box-shadow: 0 0 0 2px rgba(0, 136, 12, 0.2);
    }
  }
  
  .icon-button,
  .send-button {
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    padding: 8px;
    cursor: pointer;
    color: var(--text-secondary);
    transition: color 0.2s;
    flex-shrink: 0;
    
    &:hover {
      color: var(--gojek-green);
    }
  }
  
  .send-button {
    background-color: var(--gojek-green);
    color: white;
    border-radius: 50%;
    width: 44px;
    height: 44px;
    transition: background-color 0.2s;
    
    &:hover {
      background-color: #00730a;
    }
  }
}

.typing-indicator {
  span {
    height: 10px;
    width: 10px;
    float: left;
    margin: 0 1px;
    background-color: #9E9EA1;
    display: block;
    border-radius: 50%;
    opacity: 0.4;
    animation: 1s blink infinite;
    
    &:nth-child(2) {
      animation-delay: .2s;
    }
    
    &:nth-child(3) {
      animation-delay: .4s;
    }
  }
}

@keyframes blink {
  50% {
    opacity: 1;
  }
}
</style>
